import React, { useMemo, useState } from 'react';
import { useStore, useSupplierById } from '../store-hybrid';
import { BrandLogo } from '../components/BrandLogo';
import { ImageUpload } from '../components/ImageUpload';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { handleImageError } from '../utils/imageUtils';
import { useReadOnly } from '../hooks/useReadOnly';
import { BrandId, ModelId, Car } from '../types';

export const GaragePage: React.FC = () => {
  const { brands, models, suppliers, cars, addCar, updateCar, deleteCar, markCarAsAvailable } = useStore();
  const { isReadOnly, showDemoAlert } = useReadOnly();
  
  // Check if current user is demo user
  const isDemoUser = useMemo(() => {
    return isReadOnly;
  }, [isReadOnly]);
  
  // Form states
  const [isAddingCar, setIsAddingCar] = useState(false);
  const [editingCar, setEditingCar] = useState<Car | null>(null);
  const [formData, setFormData] = useState({
    brandId: '' as BrandId,
    modelId: '' as ModelId,
    year: '',
    transmission: '' as 'manual' | 'automatic' | '',
    fuel: '' as 'gasoline' | 'diesel' | 'hybrid' | 'electric' | 'other' | '',
    dailyRate: '',
    salePrice: '',
    purchasePrice: '',
    supplierId: '',
    plate: '',
    color: '',
    mileageKm: '',
    images: [] as string[],
  });

  // Filter states
  const [filterBrand, setFilterBrand] = useState<BrandId>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'available' | 'rented' | 'for-sale' | 'paid' | 'unpaid'>('all');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [soldSectionExpanded, setSoldSectionExpanded] = useState(false);

  const brandModels = useMemo(() => 
    models.filter(model => model.brandId === formData.brandId),
    [models, formData.brandId]
  );

  // Filter cars in garage
  const garageCars = useMemo(() => {
    let filtered = cars.filter(car => car.inGarage);
    
    if (filterBrand) {
      filtered = filtered.filter(car => car.brandId === filterBrand);
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(car => {
        switch (filterStatus) {
          case 'available': return !car.isRented && !car.isSold;
          case 'rented': return car.isRented;
          case 'for-sale': return car.isForSale;
          case 'paid': return car.isPaid;
          case 'unpaid': return !car.isPaid;
          default: return true;
        }
      });
    }
    
    return filtered;
  }, [cars, filterBrand, filterStatus]);

  // Separate paid and unpaid cars
  const paidCars = useMemo(() => 
    garageCars.filter(car => car.isPaid && !car.isSold),
    [garageCars]
  );
  
  const unpaidCars = useMemo(() => 
    garageCars.filter(car => !car.isPaid && !car.isSold),
    [garageCars]
  );
  
  const soldCars = useMemo(() => 
    cars.filter(car => car.isSold), // Include all sold cars, not just garage cars
    [cars]
  );

  const resetForm = () => {
    setFormData({
      brandId: '' as BrandId,
      modelId: '' as ModelId,
      year: '',
      transmission: '' as 'manual' | 'automatic' | '',
      fuel: '' as 'gasoline' | 'diesel' | 'hybrid' | 'electric' | 'other' | '',
      dailyRate: '',
      salePrice: '',
      purchasePrice: '',
      supplierId: '',
      plate: '',
      color: '',
      mileageKm: '',
      images: [],
    });
    setIsAddingCar(false);
    setEditingCar(null);
  };

  const handleSubmit = () => {
    if (isReadOnly) {
      showDemoAlert();
      return;
    }
    
    if (!formData.brandId || !formData.modelId) return;

    const carData: Partial<Car> = {
      year: formData.year ? parseInt(formData.year) : undefined,
      transmission: formData.transmission || undefined,
      fuel: formData.fuel || undefined,
      dailyRate: formData.dailyRate ? parseFloat(formData.dailyRate) : undefined,
      salePrice: formData.salePrice ? parseFloat(formData.salePrice) : undefined,
      purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
      supplierId: formData.supplierId || undefined,
      plate: formData.plate || undefined,
      color: formData.color || undefined,
      mileageKm: formData.mileageKm ? parseInt(formData.mileageKm) : undefined,
      images: formData.images.length > 0 ? formData.images : undefined,
    };

    if (editingCar) {
      updateCar(editingCar.id, { ...carData, brandId: formData.brandId, modelId: formData.modelId });
    } else {
      addCar(formData.brandId, formData.modelId, carData);
    }

    resetForm();
  };

  const handleEdit = (car: Car) => {
    setEditingCar(car);
    setFormData({
      brandId: car.brandId,
      modelId: car.modelId,
      year: car.year?.toString() || '',
      transmission: car.transmission || '',
      fuel: car.fuel || '',
      dailyRate: car.dailyRate?.toString() || '',
      salePrice: car.salePrice?.toString() || '',
      purchasePrice: car.purchasePrice?.toString() || '',
      supplierId: car.supplierId || '',
      plate: car.plate || '',
      color: car.color || '',
      mileageKm: car.mileageKm?.toString() || '',
      images: car.images || [],
    });
    setIsAddingCar(true);
    
    // Smooth scroll to top of page where the edit form is
    // Add a small delay to ensure the form is rendered
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleDelete = (carId: string) => {
    if (window.confirm('Are you sure you want to remove this car from the garage?')) {
      deleteCar(carId);
    }
  };

  const handleMarkAsAvailable = async (carId: string) => {
    try {
      await markCarAsAvailable(carId);
    } catch (error) {
      console.error('Failed to mark car as available:', error);
    }
  };


  const stats = [
    {
      label: 'Total Cars',
      value: garageCars.length,
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      label: 'Available',
      value: garageCars.filter(car => !car.isRented && !car.isSold).length,
      icon: (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: 'Currently Rented',
      value: garageCars.filter(car => car.isRented).length,
      icon: (
        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: 'For Sale',
      value: garageCars.filter(car => car.isForSale).length,
      icon: (
        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    {
      label: 'Paid Cars',
      value: garageCars.filter(car => car.isPaid).length,
      icon: (
        <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: 'Unpaid Cars',
      value: garageCars.filter(car => !car.isPaid).length,
      icon: (
        <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:text-white mb-2">
              🏠 Garage
            </h1>
            <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300">
              Manage your car garage - all cars available for rent or sale
            </p>
          </div>
          
          <button
            onClick={() => {
              if (isDemoUser) {
                showDemoAlert();
                return;
              }
              setIsAddingCar(true);
              // Smooth scroll to form
              setTimeout(() => {
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }, 100);
            }}
            disabled={isDemoUser}
            className={`inline-flex items-center px-4 py-2 rounded-xl transition-colors font-medium ${
              isDemoUser 
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {isDemoUser ? 'Add Car (Demo Mode)' : 'Add Car to Garage'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50">
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Collapsible Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8 overflow-hidden">
          {/* Filter Header - Always Visible */}
          <div 
            className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
                {!filtersExpanded && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {filterBrand ? brands.find(b => b.id === filterBrand)?.name : 'All Brands'}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {filterStatus === 'all' ? 'All Cars' : 
                       filterStatus === 'available' ? 'Available' :
                       filterStatus === 'rented' ? 'Rented' :
                       filterStatus === 'for-sale' ? 'For Sale' :
                       filterStatus === 'paid' ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                )}
              </div>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Filter Content - Expandable */}
          <div className={`transition-all duration-300 ease-in-out ${filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Brand
              </label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value as BrandId)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Cars</option>
                <option value="available">Available</option>
                <option value="rented">Currently Rented</option>
                <option value="for-sale">For Sale</option>
                    <option value="paid">Paid Cars</option>
                    <option value="unpaid">Unpaid Cars</option>
              </select>
                </div>
              </div>

              {/* Clear Filters Button */}
              {(filterBrand || filterStatus !== 'all') && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setFilterBrand('');
                      setFilterStatus('all');
                    }}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Car Form */}
        {isAddingCar && (
          <div className={`rounded-2xl shadow-sm border p-6 mb-8 ${
            editingCar 
              ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
              : 'bg-white border-gray-100 dark:bg-gray-800 dark:border-gray-700'
          }`}>
            <div className="flex items-center space-x-2 mb-4">
              {editingCar && (
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
              )}
              <h3 className={`text-lg font-semibold ${
                editingCar 
                  ? 'text-blue-900 dark:text-blue-100' 
                  : 'text-gray-900 dark:text-white'
              }`}>
              {editingCar ? 'Edit Car' : 'Add Car to Garage'}
            </h3>
              {editingCar && (
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs font-medium rounded-full">
                  Editing Mode
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand *
                </label>
                <select
                  value={formData.brandId}
                  onChange={(e) => {
                    setFormData({ ...formData, brandId: e.target.value as BrandId, modelId: '' as ModelId });
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Brand</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model *
                </label>
                <select
                  value={formData.modelId}
                  onChange={(e) => setFormData({ ...formData, modelId: e.target.value as ModelId })}
                  disabled={!formData.brandId}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                >
                  <option value="">Select Model</option>
                  {brandModels.map((model) => (
                    <option key={model.id} value={model.id}>{model.name}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="2023"
                  min="1900"
                  max="2030"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="Red"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* License Plate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Plate
                </label>
                <input
                  type="text"
                  value={formData.plate}
                  onChange={(e) => setFormData({ ...formData, plate: e.target.value })}
                  placeholder="ABC-123"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Mileage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mileage (km)
                </label>
                <input
                  type="number"
                  value={formData.mileageKm}
                  onChange={(e) => setFormData({ ...formData, mileageKm: e.target.value })}
                  placeholder="50000"
                  min="0"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Daily Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Rate (DA) <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="number"
                  value={formData.dailyRate}
                  onChange={(e) => setFormData({ ...formData, dailyRate: e.target.value })}
                  placeholder="145"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Sale Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sale Price (DA) <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="number"
                  value={formData.salePrice}
                  onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                  placeholder="25000"
                  min="0"
                  step="100"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Purchase Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Price (DA) <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="number"
                  value={formData.purchasePrice}
                  onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                  placeholder="20000"
                  min="0"
                  step="100"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Supplier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplier <span className="text-gray-400">(optional)</span>
                </label>
                <select
                  value={formData.supplierId}
                  onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="mb-6">
              <ImageUpload
                images={formData.images}
                onImagesChange={(images: string[]) => setFormData({ ...formData, images })}
                maxImages={5}
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleSubmit}
                disabled={isDemoUser || !formData.brandId || !formData.modelId}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDemoUser 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {isDemoUser ? 'Demo Mode (Read Only)' : (editingCar ? 'Update Car' : 'Add to Garage')}
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Cars Grid - Separated by Payment Status */}
        {garageCars.length > 0 ? (
          <div className="space-y-8">
            {/* Paid Cars Section */}
            {paidCars.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Paid Cars ({paidCars.length})
                    </h2>
                  </div>
                  <div className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                    Available for both rental and sale
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paidCars.map((car) => (
                    <GarageCarCard
                      key={car.id}
                      car={car}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onMarkAsAvailable={handleMarkAsAvailable}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Unpaid Cars Section */}
            {unpaidCars.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Unpaid Cars ({unpaidCars.length})
                    </h2>
                  </div>
                  <div className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                    Available for sale only (rental disabled)
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {unpaidCars.map((car) => (
                    <GarageCarCard
                      key={car.id}
                      car={car}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onMarkAsAvailable={handleMarkAsAvailable}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Sold Cars Section - Expandable */}
            {soldCars.length > 0 && (
              <div>
                <div 
                  className="flex items-center mb-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-3 rounded-lg transition-colors"
                  onClick={() => setSoldSectionExpanded(!soldSectionExpanded)}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Sold Cars ({soldCars.length})
                    </h2>
                  </div>
                  <div className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                    No longer in garage
                  </div>
                  <svg 
                    className={`ml-auto w-5 h-5 text-gray-400 transition-transform duration-200 ${soldSectionExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {soldSectionExpanded && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {soldCars.map((car) => (
              <GarageCarCard
                key={car.id}
                car={car}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMarkAsAvailable={handleMarkAsAvailable}
              />
            ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {cars.filter(car => car.inGarage).length === 0 
                ? 'No cars in garage yet' 
                : 'No cars match your filters'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {cars.filter(car => car.inGarage).length === 0 
                ? 'Get started by adding your first car to the garage.'
                : 'Try adjusting your filters or add a new car.'
              }
            </p>
            <button
              onClick={() => {
                setIsAddingCar(true);
                // Smooth scroll to form
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }, 100);
              }}
              className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Add Your First Car
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// Garage Car Card Component
const GarageCarCard: React.FC<{ 
  car: Car; 
  onEdit: (car: Car) => void; 
  onDelete: (carId: string) => void;
  onMarkAsAvailable?: (carId: string) => void;
}> = ({ car, onEdit, onDelete, onMarkAsAvailable }) => {
  const { brands, models, updateCar } = useStore();
  const brand = brands.find(b => b.id === car.brandId);
  const model = models.find(m => m.id === car.modelId);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMultipleImages = car.images && car.images.length > 1;
  const totalImages = car.images?.length || 0;

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const togglePaymentStatus = () => {
    updateCar(car.id, { isPaid: !car.isPaid });
  };

  const getStatusColor = () => {
    if (car.isRented) return 'bg-orange-100 text-orange-800';
    if (car.isSold) return 'bg-red-100 text-red-800';
    if (car.isForSale) return 'bg-purple-100 text-purple-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = () => {
    if (car.isSold) return 'Out of Garage';
    if (car.isRented) return 'Out of Garage';
    if (car.isForSale) return 'For Sale';
    return 'Available';
  };

  const getPaymentStatusColor = () => {
    return car.isPaid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800';
  };

  const getPaymentStatusText = () => {
    return car.isPaid ? 'Paid' : 'Unpaid';
  };

  return (
    <div className={`rounded-2xl shadow-sm border p-6 transition-all duration-300 ${
      car.isSold
        ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 opacity-60 grayscale'
        : car.isRented 
          ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 opacity-75' 
          : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:shadow-md'
    }`}>
      {/* Car Image */}
      <div className="w-full h-32 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl mb-4 overflow-hidden relative group">
        {car.images && car.images.length > 0 ? (
          <>
          <img 
              src={car.images[currentImageIndex]} 
            alt={`${brand?.name} ${model?.name}`}
              className={`w-full h-full object-cover transition-opacity duration-300 ${car.isRented ? 'grayscale' : ''}`}
              onError={(e) => handleImageError(e, 'car', 400, 300)}
            />
            
            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        
        {/* Rental Status Badge */}
        {car.isRented && car.rentedUntil && (
          <div className="absolute top-2 right-2 z-10">
            <div className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
              {(() => {
                // Handle Firestore Timestamp objects
                let rentedUntil: Date;
                if (typeof car.rentedUntil === 'object' && 'seconds' in car.rentedUntil) {
                  rentedUntil = new Date((car.rentedUntil as any).seconds * 1000);
                } else {
                  rentedUntil = new Date(car.rentedUntil);
                }
                
                const now = new Date();
                const msUntilReturn = rentedUntil.getTime() - now.getTime();
                const hoursUntilReturn = msUntilReturn / (1000 * 60 * 60);
                
                if (hoursUntilReturn < 0) {
                  const hoursOverdue = Math.floor(Math.abs(hoursUntilReturn));
                  return `${hoursOverdue}h overdue`;
                } else if (hoursUntilReturn < 24) {
                  const hours = Math.floor(hoursUntilReturn);
                  const minutes = Math.floor((msUntilReturn % (1000 * 60 * 60)) / (1000 * 60));
                  return hours > 0 ? `${hours}h ${minutes}m left` : `${Math.max(1, minutes)}m left`;
                } else {
                  const days = Math.floor(hoursUntilReturn / 24);
                  const hours = Math.floor(hoursUntilReturn % 24);
                  return `${days}d ${hours}h left`;
                }
              })()}
            </div>
          </div>
        )}
        
        {/* Rented Overlay */}
        {car.isRented && (
          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
            <div className="bg-white bg-opacity-90 px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-gray-700">🚗 Currently Rented</span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <BrandLogo brandId={car.brandId} size="md" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                {brand?.name} {model?.name}
              </h3>
              {car.year && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{car.year} • {car.color}</p>
              )}
            </div>
          </div>
          
          <div className="flex flex-col space-y-1">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor()}`}>
              {getPaymentStatusText()}
            </span>
          </div>
        </div>

        {/* Car Details */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          {car.plate && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400">{car.plate}</span>
            </div>
          )}
          
          {car.mileageKm && (
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400">{car.mileageKm.toLocaleString()} km</span>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex justify-between items-center text-sm">
            {car.dailyRate && (
              <div>
                <span className="font-semibold text-blue-600">{formatSimpleCurrency(car.dailyRate || 0)}/day</span>
              </div>
            )}
            {car.salePrice && (
              <div>
                <span className="font-semibold text-green-600">{formatSimpleCurrency(car.salePrice || 0)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Status Toggle */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Status:</span>
            <button
              onClick={togglePaymentStatus}
              disabled={car.isRented}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                car.isPaid ? 'bg-emerald-600' : 'bg-gray-200'
              } ${car.isRented ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  car.isPaid ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {car.isPaid 
              ? 'Available for rental and sale' 
              : 'Sale only (rental disabled)'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex space-x-2 pt-3">
          {car.isSold && onMarkAsAvailable ? (
            <>
              <button
                onClick={() => onMarkAsAvailable(car.id)}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors text-green-600 bg-green-50 hover:bg-green-100"
              >
                ↩️ Mark Available
              </button>
              <button
                onClick={() => onEdit(car)}
                className="px-3 py-2 text-sm font-medium rounded-lg transition-colors text-primary-600 bg-primary-50 hover:bg-primary-100"
              >
                Edit
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onEdit(car)}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors text-primary-600 bg-primary-50 hover:bg-primary-100"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(car.id)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  car.isRented
                    ? 'text-gray-500 dark:text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-red-600 bg-red-50 hover:bg-red-100'
                }`}
                disabled={car.isRented}
              >
                {car.isRented ? 'Can\'t Remove' : 'Remove'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
